import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { addDoc, collection, doc, getDoc, getDocs, setDoc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { RootState } from "@/store/store";
import { pick } from "lodash";
import { OrganizationAlias, Website } from "@/types";
import { ApiError } from "@/types/api";
import { DEFAULT_WEBSITE_TEMPLATE } from "@/constants/configs";

interface OrganizationWebsiteQuery {
  organizationId: string | SkipToken;
  websiteId: string | SkipToken;
}

interface WebsiteUpdateMutation {
  organizationId: string | SkipToken;
  websiteId?: string | SkipToken;
  website: Partial<Website>;
}

export const websiteApi = createApi({
  reducerPath: "website",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Website", "Alias"],
  endpoints: (builder) => ({
    fetchOrganizationWebsites: builder.query<Website, string | SkipToken>({
      queryFn: async (organizationId) => {
        if (!organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        const collectionRef = collection(db, `organizations/${String(organizationId)}/websites`);

        const websites = await getDocs(collectionRef).then((querySnapshot) => {
          return querySnapshot.docs.reduce((acc, doc: any) => {
            return [...acc, { ...doc.data(), id: doc.id }];
          }, [] as Website[]);
        });

        if (!websites?.length) {
          return {
            error: {
              status: 404,
              statusText: "Sitio web no encontrado",
            },
          };
        }

        return {
          data: websites,
        };
      },
      providesTags: ["Website"],
    }),
    fetchOrganizationWebsite: builder.query<Website, OrganizationWebsiteQuery>({
      queryFn: async ({ organizationId, websiteId }) => {
        if (!organizationId || !websiteId) {
          return {
            error: {
              status: 400,
              statusText: "Parámetros incorrectos",
            },
          };
        }

        const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            data: {
              ...snapshot.data(),
              id: snapshot.id,
            },
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Sitio web no encontrado",
          },
        };
      },
      providesTags: ["Website"],
    }),
    updateWebsiteStyles: builder.mutation<void, WebsiteUpdateMutation>({
      queryFn: async ({ organizationId, websiteId, website }, { getState }) => {
        const store = getState() as RootState;

        const ALLOWED_FIELDS = ["styles"];

        if (!store?.auth?.user?.uid || !organizationId || !websiteId) {
          return {
            error: {
              status: 400,
              statusText: "Parámetros incorrectos",
            },
          };
        }

        try {
          const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);

          const payload = {
            ...pick(website, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          await updateDoc(docRef, payload);
        } catch {
          return {
            error: {
              status: 400,
              statusText: "Error al actualizar el sitio web",
            },
          };
        }
      },
      invalidatesTags: ["Website"],
    }),
    updateWebsite: builder.mutation<void, WebsiteUpdateMutation>({
      queryFn: async ({ organizationId, websiteId, website }, { getState }) => {
        const store = getState() as RootState;

        const ALLOWED_FIELDS = ["title", "description", "template", "styles", "isPublished", "data", "metaData", "menu", "lastUpdatedBy", "lastUpdatedAt", "settings"];

        if (!store?.auth?.user?.uid || !organizationId || !websiteId) {
          return {
            error: {
              status: 400,
              statusText: "Parámetros incorrectos",
            },
          };
        }

        try {
          const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);
          const payload = {
            ...pick(website, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          await updateDoc(docRef, payload);
        } catch {
          return {
            error: {
              status: 400,
              statusText: "Error al actualizar el sitio web",
            },
          };
        }
      },
      invalidatesTags: ["Website"],
    }),
    createWebsite: builder.mutation<Website, WebsiteUpdateMutation>({
      queryFn: async ({ organizationId, website }, { getState }) => {
        const store = getState() as RootState;

        const ALLOWED_FIELDS = ["title", "template", "data"];

        if (!store?.auth?.user?.uid || !organizationId || !website) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        try {
          const collectionRef = collection(db, `organizations/${String(organizationId)}/websites`);

          const payload = {
            ...pick(website, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          if (!payload?.template) {
            payload.template = DEFAULT_WEBSITE_TEMPLATE;
          }

          const snapshot = await addDoc(collectionRef, payload);

          return {
            data: snapshot.id,
          };
        } catch {
          return {
            error: {
              status: 400,
              statusText: "Error al actualizar el sitio web",
            },
          };
        }
      },
      invalidatesTags: ["Website"],
    }),
    fetchOrganizationAlias: builder.query<OrganizationAlias, { alias: string }>({
      queryFn: async (alias) => {
        if (!alias) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        const docRef = doc(db, `aliases/${String(alias)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            data: {
              ...snapshot.data(),
              alias: snapshot.id,
            },
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Alias no encontrada",
          },
        };
      },
      providesTags: ["Alias"],
    }),
    createOrganizationAlias: builder.mutation<OrganizationAlias, { alias: string; organizationId: string }>({
      queryFn: async ({ alias, organizationId }, { getState }) => {
        const store = getState() as RootState;

        if (!store?.auth?.user?.uid || !alias || !organizationId) {
          return {
            error: {
              status: 400,
              statusText: "Consulta inválida",
            },
          };
        }

        const payload = {
          organizationId,
          createdAt: Date.now(),
          createdBy: store.auth.user.uid,
        };

        try {
          const docRef = doc(db, `aliases/${String(alias)}`);
          await setDoc(docRef, payload);

          return {
            data: {
              alias,
              organizationId,
            },
          };
        } catch {
          return {
            error: {
              status: 400,
              statusText: "Error al crear el alias",
            },
          };
        }
      },
      invalidatesTags: ["Alias"],
    }),
  }),
});

export const {
  useFetchOrganizationWebsitesQuery,
  useLazyFetchOrganizationWebsitesQuery,
  useUpdateWebsiteMutation,
  useUpdateWebsiteStylesMutation,
  useFetchOrganizationWebsiteQuery,
  useCreateWebsiteMutation,
  useFetchOrganizationAliasQuery,
  useCreateOrganizationAliasMutation,
  useLazyFetchOrganizationAliasQuery,
} = websiteApi;
